/**
 * 언어 정책 (시스템 강제) — 명세서 §6.9 / §6.1-1
 *
 * "미흡" 같은 결핍·판정 언어를 시스템 차원에서 배제한다.
 * 정책은 **차단이 아니라 대체 표현 제안**이다. 교사 작성 화면에서 실시간 경고를 띄우되
 * 저장 자체를 막지는 않는다(현장 맥락을 시스템이 전부 알 수는 없기 때문).
 */
/**
 * 금칙어 사전.
 * `장애`는 맥락 무관 사용만 문제이므로 별도 안내 문구를 둔다(§6.9).
 */
export const FORBIDDEN_TERMS = [
    {
        term: '미흡',
        suggestions: ['○○ 단계를 지나는 중이에요', '조금씩 익숙해지고 있어요'],
        reason: '판정 어휘입니다. 지금 지나는 단계로 바꿔 적어 주세요.',
    },
    {
        term: '부진',
        suggestions: ['자기 속도로 나아가고 있어요', '반복해서 시도해 보고 있어요'],
        reason: '판정 어휘입니다.',
    },
    {
        term: '느림',
        suggestions: ['천천히 살펴보며 다가가요', '충분히 관찰한 뒤 시작해요'],
        reason: '속도를 우열로 읽히게 합니다.',
    },
    {
        term: '느려',
        suggestions: ['천천히 살펴보며 다가가요', '충분히 관찰한 뒤 시작해요'],
        reason: '속도를 우열로 읽히게 합니다.',
    },
    {
        term: '뒤처',
        suggestions: ['자기 속도로 자라고 있어요', '또래와 다른 순서로 익혀 가고 있어요'],
        reason: '또래 비교를 전제한 표현입니다(§6.1-3 자기 비교 원칙).',
    },
    {
        term: '문제',
        suggestions: ['살펴볼 점', '함께 지켜보는 부분'],
        reason: '아이를 문제로 지칭하지 않습니다.',
        // "문제를 스스로 해결해요"처럼 문제해결력을 말하는 맥락은 결핍 언어가 아니다.
        allowContext: ['문제[가를을]?[^,.]{0,12}(해결|풀)', '문제해결'],
    },
    {
        term: '못해',
        suggestions: ['아직 시작 전이에요', '관심을 보이기 시작했어요'],
        reason: '결핍 언어입니다.',
    },
    {
        term: '못함',
        suggestions: ['아직 시작 전이에요', '관심을 보이기 시작했어요'],
        reason: '결핍 언어입니다.',
    },
    {
        term: '안 됨',
        suggestions: ['조금 더 익숙해지는 중이에요'],
        reason: '결핍 언어입니다.',
    },
    {
        term: '산만',
        suggestions: ['여러 활동에 두루 관심을 보여요', '관심이 자주 옮겨 다녀요'],
        reason: '기질을 부정적으로 규정합니다.',
    },
    // "떨어" 두 글자만으로는 물건이 떨어지는 것과 능력이 떨어진다는 말을 구분할 수 없다
    // ("공이 떨어져요" vs "능력이 떨어져요"). 무엇에 대해 그렇다는 것인지까지 포함해 등록한다.
    {
        term: '능력이 떨어',
        suggestions: ['지금 지나는 단계를 적어 주세요'],
        reason: '평가 표현입니다. 아이가 지나는 지점으로 바꿔 적어 주세요.',
    },
    {
        term: '수준이 떨어',
        suggestions: ['지금 지나는 단계를 적어 주세요'],
        reason: '평가 표현입니다.',
    },
    {
        term: '발달이 떨어',
        suggestions: ['자기 속도로 나아가고 있어요'],
        reason: '평가 표현입니다.',
    },
    {
        term: '보다 떨어',
        suggestions: ['자기 속도로 자라고 있어요'],
        reason: '또래 비교를 전제한 표현입니다(§6.1-3 자기 비교 원칙).',
    },
    {
        term: '장애',
        suggestions: ['(진단 관련 내용은 기록 대신 면담으로 안내해 주세요)'],
        reason: '진단 어휘는 교사가 기록에 쓰지 않습니다. 필요한 경우 면담(§5.15)으로 안내합니다.',
        // §6.9가 "장애(맥락 무관 사용)"이라 적은 그대로 — 장애물은 다른 뜻이다.
        allowContext: ['장애물'],
    },
];
/**
 * 텍스트에서 금칙어를 찾아 경고 목록을 반환한다.
 * 저장을 막지 않으므로 API·UI 모두 "경고"로만 취급한다.
 */
export function checkLanguage(text) {
    if (!text)
        return [];
    const warnings = [];
    for (const entry of FORBIDDEN_TERMS) {
        const allowed = entry.allowContext ? allowedRanges(text, entry.allowContext) : [];
        let from = 0;
        for (;;) {
            const index = text.indexOf(entry.term, from);
            if (index === -1)
                break;
            from = index + entry.term.length;
            // 예외 맥락에 덮인 자리면 다른 뜻이므로 넘어간다.
            if (allowed.some(([start, end]) => index >= start && from <= end))
                continue;
            warnings.push({
                term: entry.term,
                index,
                suggestions: entry.suggestions,
                reason: entry.reason,
            });
        }
    }
    return warnings.sort((a, b) => a.index - b.index);
}
/** 예외 패턴이 실제로 걸린 [시작, 끝) 구간들 */
function allowedRanges(text, patterns) {
    const ranges = [];
    for (const pattern of patterns) {
        // 전역 정규식은 lastIndex 상태를 갖기 때문에 매번 새로 만든다.
        const regex = new RegExp(pattern, 'g');
        for (const match of text.matchAll(regex)) {
            if (match.index === undefined)
                continue;
            ranges.push([match.index, match.index + match[0].length]);
        }
    }
    return ranges;
}
export function hasForbiddenLanguage(text) {
    return checkLanguage(text).length > 0;
}
/** 작성 화면 사이드에 상시 노출하는 대체 표현 가이드 (§6.9) */
export const REPHRASE_GUIDE = [
    { before: '아직 못해요', after: '○○ 단계를 지나는 중이에요' },
    { before: '관심이 없어요', after: '아직 지켜보는 중이에요' },
    { before: '자주 울어요', after: '마음을 표현하는 방법을 익혀 가고 있어요' },
    { before: '친구를 때려요', after: '마음을 말로 전하는 연습을 하고 있어요' },
    { before: '집중을 못해요', after: '관심이 여러 곳으로 옮겨 다녀요' },
    { before: '또래보다 늦어요', after: '자기 속도로 한 걸음씩 나아가고 있어요' },
];
//# sourceMappingURL=language-policy.js.map